import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ChevronRight, CircleAlert, Database, Flag, LoaderCircle, RefreshCw, UserRound, Workflow } from 'lucide-react';
import { Badge } from '../design/ui';
import { fmtMs, fmtCost, fmtDateTime, compactJson, statusClass, runTitle, runStatusLabel } from './aiTrace/format';
import './AiRunPanel.css';

// The « Runs IA » tab of a ticket (docs/ai-trace-spec.md §6.2, §13 step 14):
// a compact, chronological list of the ticket's runs. Opening a run — or
// the whole chronology — happens in the full-screen timeline overlay
// (AiTimelineOverlay), never inline: one place to explore, left → right.
//
// It also carries the operator's « re-run » form (spec §6.3): corrected
// identifiers (tracking number, order reference, client, carrier named by
// the sender) are handed to the ticket, the tenant's data sources run again
// on them and the AI processing restarts on the refreshed facts.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const EMPTY_FORM = { tracking_number: '', order_number: '', client_name: '', carrier_name: '' };

// What the form starts with: the identifiers the ticket carries today. The
// carrier field is a hint (the carrier the sender names), never prefilled
// from the ticket: a stale carrier would contradict a corrected order.
const prefillFrom = (ticket) => {
    const ctx = ticket && ticket.advanced_context && typeof ticket.advanced_context === 'object' ? ticket.advanced_context : {};
    const client = ticket && ticket.metadata && ticket.metadata.client && typeof ticket.metadata.client === 'object' ? ticket.metadata.client : {};
    return {
        tracking_number: (ticket && ticket.tracking_number) || ctx.tracking_number || '',
        // The order number the form or the agent recorded (num_commande),
        // else the one an integration put in the context (KI-193).
        order_number: ctx.num_commande || ctx.order_number || '',
        client_name: ctx.shop_name || client.name || '',
        carrier_name: '',
    };
};

const AiRunPanel = ({ ticketId, ticket, onOpenRun, onOpenTimeline, onRerunDone }) => {
    const { t, i18n } = useTranslation();
    const [runs, setRuns] = useState(null);
    const [total, setTotal] = useState(0);
    const [listError, setListError] = useState(null);
    const [rerunOpen, setRerunOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [restartAi, setRestartAi] = useState(true);
    const [rerunBusy, setRerunBusy] = useState(false);
    const [rerunResult, setRerunResult] = useState(null);
    const [rerunError, setRerunError] = useState(null);

    const loadList = useCallback(async () => {
        if (!ticketId) return;
        setListError(null);
        try {
            const response = await axios.get(`/api/tickets/${ticketId}/ai-runs?limit=100`, authHeaders());
            setRuns(response.data.runs || []);
            setTotal(response.data.total || 0);
        } catch (e) {
            setRuns([]);
            setListError(t('aiRunPanel.loadError', 'Impossible de charger les runs IA.'));
        }
    }, [ticketId, t]);

    useEffect(() => { loadList(); }, [loadList]);

    const openRerun = () => {
        setForm(prefillFrom(ticket));
        setRerunResult(null);
        setRerunError(null);
        setRerunOpen(true);
    };

    const canSubmitRerun = Boolean((form.tracking_number || '').trim() || (form.order_number || '').trim());

    const submitRerun = async (event) => {
        event.preventDefault();
        if (rerunBusy || !canSubmitRerun) return;
        setRerunBusy(true);
        setRerunError(null);
        setRerunResult(null);
        try {
            const payload = { restart_ai: restartAi };
            Object.entries(form).forEach(([key, value]) => {
                const trimmed = (value || '').trim();
                if (trimmed) payload[key] = trimmed;
            });
            const response = await axios.post(`/api/tickets/${ticketId}/enrichment/rerun`, payload, authHeaders());
            setRerunResult(response.data);
            loadList();
            // The AI restart runs in the background: refresh the list once it
            // has had time to open its run.
            setTimeout(loadList, 5000);
            if (onRerunDone) onRerunDone(response.data);
        } catch (e) {
            const detail = e.response && e.response.data && e.response.data.detail;
            setRerunError(typeof detail === 'string' ? detail : t('aiRunPanel.rerun.error', 'La relance a échoué.'));
        } finally {
            setRerunBusy(false);
        }
    };

    const renderRerunResult = () => {
        if (!rerunResult) return null;
        const enrichment = rerunResult.enrichment || {};
        const ids = enrichment.identifiers || {};
        const parts = [];
        if (enrichment.found) {
            parts.push(t('aiRunPanel.rerun.found', { defaultValue: 'Données trouvées via {{source}}', source: enrichment.source || '?' }));
        } else {
            parts.push(t('aiRunPanel.rerun.notFound', 'Aucune donnée trouvée pour ces identifiants'));
        }
        if (enrichment.carrier_name) parts.push(`${t('aiRunPanel.rerun.carrierLabel', 'transporteur')} ${enrichment.carrier_name}`);
        if (ids.tracking_number) parts.push(`${t('aiRunPanel.rerun.trackingLabel', 'suivi')} ${ids.tracking_number}`);
        if (ids.client_name) parts.push(`${t('aiRunPanel.rerun.clientLabel', 'client')} ${ids.client_name}`);
        if (enrichment.is_shipped === '1' || enrichment.is_shipped === '0') {
            parts.push(`${t('aiRunPanel.rerun.shipped', 'expédié')} ${enrichment.is_shipped === '1' ? t('aiRunPanel.rerun.yes', 'oui') : t('aiRunPanel.rerun.no', 'non')}`);
        }
        if (enrichment.is_delivered === '1') parts.push(t('aiRunPanel.rerun.delivered', 'livré'));
        (enrichment.conflicts || []).forEach((conflict) => {
            parts.push(t('aiRunPanel.rerun.conflict', {
                defaultValue: 'conflit transporteur : l’expéditeur indique {{client}}, la commande part avec {{found}}',
                client: conflict.client_carrier || '?',
                found: conflict.effitrace_carrier || '?',
            }));
        });
        const restart = rerunResult.restart;
        return (
            <div className={`u-banner ${enrichment.found ? 'u-banner--success' : 'u-banner--warning'} arp-rerun-result`} role="status">
                <Database strokeWidth={2} aria-hidden="true" />
                <div className="u-banner__body">
                    <div>{parts.join(' · ')}</div>
                    {restart && (
                        <div className="arp-muted">
                            {t('aiRunPanel.rerun.restarted', {
                                defaultValue: 'Traitement IA relancé ({{count}} brouillon(s) écarté(s)) — le nouveau run apparaît ci-dessous dans quelques secondes.',
                                count: restart.deleted_ai_messages || 0,
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderRerunForm = () => {
        if (!rerunOpen) return null;
        const fields = [
            ['tracking_number', t('aiRunPanel.rerun.tracking', 'Numéro de suivi')],
            ['order_number', t('aiRunPanel.rerun.order', 'Référence de commande')],
            ['client_name', t('aiRunPanel.rerun.client', 'Client (tel que configuré)')],
            ['carrier_name', t('aiRunPanel.rerun.carrier', 'Transporteur indiqué par l’expéditeur (facultatif)')],
        ];
        return (
            <form className="arp-rerun" onSubmit={submitRerun}>
                <p className="arp-rerun-intro">
                    {t('aiRunPanel.rerun.intro', 'Transmettez au ticket des identifiants corrigés : les sources de données du compte (EffiTrace, connecteurs…) sont réinterrogées et, par défaut, le traitement IA est relancé sur les données à jour. Un champ vide garde la valeur actuelle du ticket.')}
                </p>
                <div className="arp-rerun-grid">
                    {fields.map(([key, label]) => (
                        <div key={key} className="u-field">
                            <label className="u-label" htmlFor={`arp-rerun-${key}`}>{label}</label>
                            <input
                                id={`arp-rerun-${key}`}
                                type="text"
                                className="u-input"
                                value={form[key]}
                                onChange={(ev) => setForm({ ...form, [key]: ev.target.value })}
                                disabled={rerunBusy}
                            />
                        </div>
                    ))}
                </div>
                <label className="arp-rerun-check">
                    <input type="checkbox" checked={restartAi} onChange={(ev) => setRestartAi(ev.target.checked)} disabled={rerunBusy} />
                    <span>{t('aiRunPanel.rerun.restart', 'Relancer le traitement IA sur les données à jour')}</span>
                </label>
                {rerunError && (
                    <div className="u-banner u-banner--danger" role="alert">
                        <CircleAlert strokeWidth={2} aria-hidden="true" />
                        <div className="u-banner__body">{rerunError}</div>
                    </div>
                )}
                {renderRerunResult()}
                <div className="arp-rerun-actions">
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setRerunOpen(false)} disabled={rerunBusy}>
                        {t('aiRunPanel.rerun.cancel', 'Fermer')}
                    </button>
                    <button type="submit" className="u-btn u-btn--primary u-btn--sm" disabled={rerunBusy || !canSubmitRerun}>
                        {rerunBusy
                            ? <LoaderCircle className="arp-spin" strokeWidth={2} aria-hidden="true" />
                            : <Database strokeWidth={1.8} aria-hidden="true" />}
                        {rerunBusy ? t('aiRunPanel.rerun.running', 'Relance en cours…') : t('aiRunPanel.rerun.submit', 'Relancer')}
                    </button>
                </div>
            </form>
        );
    };

    const renderCounters = (summary) => {
        if (!summary) return null;
        const items = [
            ['llm_calls', t('aiRunPanel.counters.llm', 'appels modèle')],
            ['tool_calls', t('aiRunPanel.counters.tools', 'outils')],
            ['decisions', t('aiRunPanel.counters.decisions', 'décisions')],
            ['effects', t('aiRunPanel.counters.effects', 'effets')],
            ['externals', t('aiRunPanel.counters.externals', 'appels externes')],
            ['automations', t('aiRunPanel.counters.automations', 'automatisations')],
            ['feedback', t('aiRunPanel.counters.feedback', 'retours')],
            ['errors', t('aiRunPanel.counters.errors', 'erreurs')],
        ].filter(([key]) => typeof summary[key] === 'number' && summary[key] > 0);
        const cost = fmtCost(summary.total_cost_usd);
        if (!items.length && !cost) return null;
        return (
            <div className="arp-counters">
                {items.map(([key, label]) => (
                    <span key={key} className={`arp-chip ${key === 'errors' ? 'arp-chip-error' : ''}`}>{summary[key]} {label}</span>
                ))}
                {cost && <span className="arp-chip">{cost}</span>}
            </div>
        );
    };

    const runTone = (status) => ({ error: 'danger', warn: 'warning', running: 'info', ok: 'success' }[statusClass(status)]);

    const renderRun = (run, nested = false) => {
        const actor = run.trigger?.actor?.type;
        const Icon = actor === 'employee' || actor === 'admin' ? UserRound : Flag;
        return (
            <li key={run._id} className={`arp-run ${nested ? 'nested' : ''}`}>
                <button type="button" className="arp-run-header" onClick={() => onOpenRun && onOpenRun(run._id)} title={run._id}>
                    <span className="arp-run-main">
                        <span className="arp-run-title">
                            <Icon strokeWidth={1.8} aria-hidden="true" />
                            <span className="arp-run-name">{runTitle(run, t)}</span>
                            {actor && actor !== 'system' && <span className="arp-muted"> · {actor}</span>}
                        </span>
                        <span className="arp-run-meta">
                            <span className={`arp-service arp-service-${run.service}`}>{run.service}</span>
                            <span>{fmtDateTime(run.started_at, i18n.language)}</span>
                            <span>{fmtMs(run.duration_ms)}</span>
                        </span>
                    </span>
                    <Badge tone={runTone(run.status)}>{runStatusLabel(run.status, t)}</Badge>
                    <ChevronRight className="arp-run-toggle" strokeWidth={2} aria-hidden="true" />
                </button>
                {renderCounters(run.summary)}
                {run.error && <div className="arp-error-inline">{run.error.type}: {compactJson(run.error.message, 140)}</div>}
                {run.children && run.children.length > 0 && (
                    <ul className="arp-children">{run.children.map((child) => renderRun(child, true))}</ul>
                )}
            </li>
        );
    };

    return (
        <section className="ai-run-panel arp" aria-labelledby="arp-title">
            <div className="arp-header">
                <h3 id="arp-title" className="arp-title">
                    {t('ticketWorkspaceUi.ai.runsTitle', 'Traitements IA')}
                    {runs && <span className="arp-count">{total}</span>}
                </h3>
                <button
                    type="button"
                    className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                    onClick={loadList}
                    title={t('aiRunPanel.refresh', 'Actualiser')}
                    aria-label={t('aiRunPanel.refresh', 'Actualiser')}
                >
                    <RefreshCw strokeWidth={1.8} />
                </button>
            </div>
            <p className="arp-intro">
                {t('aiRunPanel.intro', 'Chaque run est un traitement IA sur ce ticket : ses appels au modèle, ses outils, ses décisions, ses effets et les retours humains, dans l’ordre.')}
            </p>
            <div className="arp-header-actions">
                <button
                    type="button"
                    className={`u-btn u-btn--secondary u-btn--sm arp-rerun-toggle ${rerunOpen ? 'active' : ''}`}
                    onClick={rerunOpen ? () => setRerunOpen(false) : openRerun}
                    aria-expanded={rerunOpen}
                    title={t('aiRunPanel.rerun.toggle', 'Relancer avec de nouvelles valeurs')}
                >
                    <Database strokeWidth={1.8} aria-hidden="true" />
                    {t('aiRunPanel.rerun.toggle', 'Relancer avec de nouvelles valeurs')}
                </button>
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onOpenTimeline && onOpenTimeline()}>
                    <Workflow strokeWidth={1.8} aria-hidden="true" />
                    {t('aiTimeline.open', 'Ouvrir la chronologie')}
                </button>
            </div>
            {renderRerunForm()}
            {listError && (
                <div className="u-banner u-banner--danger" role="alert">
                    <CircleAlert strokeWidth={2} aria-hidden="true" />
                    <div className="u-banner__body">{listError}</div>
                </div>
            )}
            {runs === null && !listError && (
                <div className="arp-skeleton" aria-busy="true">
                    <span className="u-skeleton arp-skeleton__row" />
                    <span className="u-skeleton arp-skeleton__row" />
                </div>
            )}
            {runs && runs.length === 0 && !listError && (
                <p className="arp-empty">{t('aiRunPanel.empty', 'Aucun run IA enregistré pour ce ticket.')}</p>
            )}
            {runs && runs.length > 0 && <ul className="arp-list">{runs.map((run) => renderRun(run))}</ul>}
        </section>
    );
};

export default AiRunPanel;
